import React, { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import type { Option, SelectOptions } from '../forms/option';
import { Divider, Flex, Box, Text } from '@cad-arc';
import { StationContextDto } from '@mark43/cad-api';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { FormCheckbox } from '../forms/integrated-form-controls/form-checkbox';

export type FireStationInfoFormData = {
    station: Option | null;
    totalPersonnelCount: string;
    availablePersonnelCount?: string;
    assignedPersonnelCount?: string;

    resetAvailablePersonnel?: boolean;
    availablePersonnelCountOverride?: string;
};

export type StationContexts = { [key: string]: StationContextDto };

export interface FireStationInfoProps {
    stationOptions?: SelectOptions;
    stationContext?: StationContexts;
}

const MAX_PERSONNEL_COUNT = 999999;

const FireStationInfoForm: FC<FireStationInfoProps> = (props) => {
    const { control } = useFormContext<FireStationInfoFormData>();

    const hasStationSelected = useWatch({ name: 'station' });
    const isResetChecked = useWatch({ name: 'resetAvailablePersonnel' });
    const totalPersonnelCount = useWatch({ name: 'totalPersonnelCount' });
    const availablePersonnelCount = useWatch({ name: 'availablePersonnelCount' });
    const assignedPersonnelCount = useWatch({ name: 'assignedPersonnelCount' });

    const validateTotalPersonnelVsAssignedPersonnel = (newValueText: string) => {
        const newValue = parseInt(newValueText ?? '0', 10);

        if (Number.isNaN(newValue)) {
            return 'Enter a valid number';
        }

        if (newValue < assignedPersonnelCount) {
            return 'Must be greater than or equal to the Current Assigned Personnel';
        } else {
            return true;
        }
    };

    const validateMaxOverrideCount = (newValueText: string) => {
        // when isreset
        if (!isResetChecked) {
            return true;
        }
        const totalPersonnelCountValue = parseInt(totalPersonnelCount ?? '0', 10);
        const newValue = parseInt(newValueText ?? '0', 10);

        if (Number.isNaN(newValue)) {
            return 'Enter a valid number';
        }

        if (newValue > totalPersonnelCountValue) {
            return 'Must be less than or equal to Total Personnel Count';
        } else {
            return true;
        }
    };

    return (
        <FormBody>
            <Flex direction="column" gap="4">
                <Flex direction="column" marginBottom={4} gap="4">
                    <FormSelect
                        control={control}
                        name="station"
                        label="Station"
                        options={props.stationOptions ?? []}
                        isClearable={false}
                        placeholder="Select a station..."
                        rules={{
                            required: 'Required',
                        }}
                    />
                    <FormNumberInput
                        control={control}
                        name="totalPersonnelCount"
                        label="Total Personnel Count"
                        isDisabled={!hasStationSelected}
                        helpText="Active personnel on duty at station"
                        numberInputProps={{
                            style: { marginTop: 0 },
                        }}
                        rules={
                            hasStationSelected
                                ? {
                                      required: 'Required',
                                      min: {
                                          value: 0,
                                          message: 'Must be at least 0',
                                      },
                                      validate: validateTotalPersonnelVsAssignedPersonnel,
                                      max: {
                                          value: MAX_PERSONNEL_COUNT,
                                          message: `Must be at most ${MAX_PERSONNEL_COUNT}`,
                                      },
                                  }
                                : {}
                        }
                    />
                </Flex>

                <Divider />

                <Box>
                    <Box fontSize="sm" opacity={hasStationSelected ? 1 : 0.4}>
                        Current Personnel
                    </Box>
                    <Flex gap="4" opacity={hasStationSelected ? 1 : 0.4}>
                        <Flex gap="2" alignItems="baseline">
                            <Text color="text.placeholder">Available:</Text>
                            <Text fontWeight="bold" fontSize="lg">
                                {availablePersonnelCount}
                            </Text>
                        </Flex>
                        <Flex gap="2" alignItems="baseline">
                            <Text color="text.placeholder">Assigned:</Text>
                            <Text fontWeight="bold" fontSize="lg">
                                {assignedPersonnelCount}
                            </Text>
                        </Flex>
                    </Flex>
                </Box>

                <Flex direction="column" marginTop="4" gap="1">
                    <FormCheckbox
                        control={control}
                        name="resetAvailablePersonnel"
                        label="Reset available personnel count"
                        isDisabled={!hasStationSelected}
                    />
                    <FormNumberInput
                        control={control}
                        name="availablePersonnelCountOverride"
                        placeholder=""
                        aria-label="Available Personnel Override"
                        isDisabled={!hasStationSelected || !isResetChecked}
                        helpText="Available personnel is auto-calculated via response planning, update only if needed. After setting the override, auto-calculation will resume from this value."
                        rules={{
                            required: isResetChecked ? 'Required if reset is checked' : false,
                            min: {
                                value: 0,
                                message: 'Must be at least 0',
                            },
                            validate: validateMaxOverrideCount,
                        }}
                    />
                </Flex>
            </Flex>
        </FormBody>
    );
};

export default FireStationInfoForm;
